import { useRef, useState } from "react";
import { UploadSimple, Trash } from "@phosphor-icons/react";
import { api, act, useFetch } from "@/lib/api";
import { Panel, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Chips } from "@/components/studio/kit";

const PLACE = { "top-left": "Top left", "top-right": "Top right", "bottom-left": "Bottom left", "bottom-right": "Bottom right" };
const POS = { "top-left": "top-2 left-2", "top-right": "top-2 right-2", "bottom-left": "bottom-2 left-2", "bottom-right": "bottom-2 right-2" };
const W = { S: "w-[9%]", M: "w-[13%]", L: "w-[18%]" };

export const BrandKitPanel = () => {
  const { data: b, setData } = useFetch("/brand");
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const save = async (body) => { const r = await act(() => api.patch("/brand", body), "Brand kit saved"); if (r) setData(r); };
  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    const r = await act(() => api.post("/brand/logo", fd), "Logo uploaded — new visuals will carry it");
    setBusy(false);
    if (r) setData(r);
  };
  const remove = async () => { const r = await act(() => api.delete("/brand/logo"), "Logo removed"); if (r) setData(r); };
  if (!b) return null;
  return (
    <Panel title="Brand kit" data-testid="brand-kit-panel">
      <div className="grid sm:grid-cols-[200px_1fr] gap-4">
        <div className="relative aspect-square rounded-md bg-gradient-to-br from-zinc-700 to-zinc-900 overflow-hidden" data-testid="brand-preview">
          <div className="absolute inset-0 flex items-center justify-center text-[11px] text-zinc-400 font-mono">preview</div>
          {b.logo_url && <img src={b.logo_url} alt="logo" className={`absolute ${POS[b.placement]} ${W[b.size]} min-w-[28px]`} data-testid="brand-logo-img" />}
        </div>
        <div className="space-y-3">
          <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="brand-logo-input" onChange={(e) => upload(e.target.files?.[0])} />
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" className="gap-1" disabled={busy} data-testid="brand-logo-upload-btn" onClick={() => file.current?.click()}><UploadSimple /> {busy ? "Uploading…" : b.logo_url ? "Replace logo" : "Upload logo"}</Button>
            {b.logo_url && <Button size="sm" variant="ghost" className="gap-1" data-testid="brand-logo-remove-btn" onClick={remove}><Trash /> Remove</Button>}
          </div>
          <div className="text-[11px] text-zinc-500">PNG with a transparent background works best (JPG/WebP ok, up to 5 MB).</div>
          <Field label="Position"><Chips value={PLACE[b.placement]} onChange={(v) => save({ placement: Object.keys(PLACE).find((k) => PLACE[k] === v) })} options={Object.values(PLACE)} testid="brand-place" /></Field>
          <Field label="Size"><Chips value={b.size} onChange={(v) => save({ size: v })} options={["S", "M", "L"]} testid="brand-size" /></Field>
          {[["apply_images", "Stamp on new images"], ["apply_carousels", "Stamp on carousel slides"], ["apply_newsletters", "Show in newsletter header"]].map(([k, l]) => (
            <label key={k} className="flex items-center justify-between text-sm"><span>{l}</span><Switch data-testid={`brand-${k}`} checked={b[k]} onCheckedChange={(v) => save({ [k]: v })} /></label>
          ))}
          <div className="text-[11px] text-zinc-500">Applies to visuals made from now on. Videos keep the AI's own frames.</div>
        </div>
      </div>
    </Panel>
  );
};
